import React, { useState } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { jsPDF } from 'jspdf';

const MAIN   = '#6d4e36';
const SOCIETES_LIST = ['PISTAS', 'RUTAS', 'SEPGB', 'ELITE PARCS', 'CADEX', 'BROSTOL', 'SIEGE'];
const OPERATEURS    = ['IAM', 'INWI', 'ORANGE'];
const STATUTS_TYPE  = ['Carte SIM', 'Routeur ADSL', 'Fibre Optique', 'Modem 4G'];

const inputStyle = {
  border: '1px solid #d8c8b8', borderRadius: 7,
  fontSize: '0.85rem', padding: '6px 10px',
  width: '100%', outline: 'none'
};
const labelStyle = { fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' };
const btnSm = (color, bg = 'transparent') => ({
  border: `1px solid ${color}`, background: bg,
  color: bg === 'transparent' ? color : '#fff',
  borderRadius: 6, padding: '6px 16px', fontSize: '0.83rem',
  cursor: 'pointer', fontWeight: 600
});

/* ── helpers PDF ─────────────────────────────────────────────────────────── */

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fieldLine = (doc, x, y, label, value, maxWidth = 160) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(80, 60, 40);
  doc.text(label + ' :', x, y);

  const lw = doc.getTextWidth(label + ' :') + 3;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 30, 30);
  if (value) {
    doc.text(String(value), x + lw, y, { maxWidth: maxWidth - lw });
  } else {
    doc.setDrawColor(180, 160, 140);
    doc.line(x + lw, y + 1, x + maxWidth, y + 1);
  }
};

const sectionHeader = (doc, x, y, text, pageWidth) => {
  doc.setFillColor(109, 78, 54);
  doc.roundedRect(x, y - 5, pageWidth - x * 2, 8, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(255, 255, 255);
  doc.text(text, x + 4, y);
  doc.setTextColor(30, 30, 30);
  return y + 8;
};

const sigBox = (doc, x, y, label, sublabel, w = 72) => {
  doc.setDrawColor(180, 160, 140);
  doc.roundedRect(x, y, w, 28, 2, 2);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(80, 60, 40);
  doc.text(label, x + w / 2, y + 6, { align: 'center' });
  if (sublabel) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(150, 130, 110);
    doc.text(sublabel, x + w / 2, y + 11, { align: 'center' });
  }
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor(180, 160, 140);
  doc.text('Signature', x + w / 2, y + 24, { align: 'center' });
};

const pageHeader = (doc, title, subtitle, pageWidth) => {
  /* Bande couleur */
  doc.setFillColor(109, 78, 54);
  doc.rect(0, 0, pageWidth, 18, 'F');
  doc.setFillColor(139, 94, 60);
  doc.rect(0, 18, pageWidth, 3, 'F');

  /* Titre */
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text(title, pageWidth / 2, 10, { align: 'center' });

  if (subtitle) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, pageWidth / 2, 16, { align: 'center' });
  }

  /* Référence + Date */
  doc.setFontSize(7.5);
  doc.setTextColor(150, 130, 110);
  doc.text(`Édité le ${fmtDate(new Date())}`, pageWidth - 14, 27, { align: 'right' });

  doc.setTextColor(30, 30, 30);
  return 32;
};

/* ═══════════════════════════════════════════════════════════════════════════
   PDF 1 — Demande d'attribution d'un numéro professionnel
═══════════════════════════════════════════════════════════════════════════ */
export const generateDemandeNumero = (data) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw  = doc.internal.pageSize.getWidth();
  const m   = 14;
  let   y   = pageHeader(doc, "DEMANDE D'ATTRIBUTION D'UN NUMÉRO PROFESSIONNEL",
                          'ShemsPaie — Gestion Téléphonie', pw);

  /* ── Identification du demandeur ── */
  y = sectionHeader(doc, m, y, '1. IDENTIFICATION DU DEMANDEUR', pw) + 4;
  fieldLine(doc, m, y,      'Société',         data.societe);        y += 8;
  fieldLine(doc, m, y,      'Nom & Prénom',    data.nom_prenom);     y += 8;
  fieldLine(doc, m, y,      'Fonction',        data.qualification);  y += 8;
  fieldLine(doc, m, y,      'Bureau / Site',   data.bureau);         y += 8;
  y += 4;

  /* ── Caractéristiques souhaitées ── */
  y = sectionHeader(doc, m, y, '2. CARACTÉRISTIQUES SOUHAITÉES', pw) + 4;
  fieldLine(doc, m, y,      'Opérateur',       data.operateur);      y += 8;
  fieldLine(doc, m, y,      "Type de ligne",   data.type_ligne);     y += 8;
  fieldLine(doc, m, y,      'Forfait',         data.forfait);        y += 8;
  y += 4;

  /* ── Justification ── */
  y = sectionHeader(doc, m, y, '3. MOTIF DE LA DEMANDE', pw) + 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(30, 30, 30);
  if (data.motif) {
    const lines = doc.splitTextToSize(data.motif, pw - m * 2);
    doc.text(lines, m, y);
    y += lines.length * 5 + 2;
  } else {
    doc.setDrawColor(180, 160, 140);
    for (let i = 0; i < 3; i++) { doc.line(m, y + 1, pw - m, y + 1); y += 6; }
  }
  y += 6;

  /* ── Engagements ── */
  doc.setFillColor(248, 243, 238);
  doc.roundedRect(m, y, pw - m * 2, 14, 2, 2, 'F');
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 80, 60);
  const eng = "Je soussigné(e) m'engage à utiliser ce numéro professionnel exclusivement dans le cadre de mes fonctions et à en assurer la bonne gestion conformément à la politique téléphonie de l'entreprise.";
  const engLines = doc.splitTextToSize(eng, pw - m * 2 - 6);
  doc.text(engLines, m + 3, y + 5);
  y += 20;

  /* ── Date + Signatures ── */
  y = sectionHeader(doc, m, y, '4. DATE ET SIGNATURES', pw) + 6;
  fieldLine(doc, m, y, 'Date de la demande', data.date_demande ? fmtDate(data.date_demande) : '');
  y += 12;
  const bw = 57, gap = 5;
  sigBox(doc, m,                  y, 'Signature du demandeur',         data.nom_prenom || '', bw);
  sigBox(doc, m + bw + gap,       y, 'Visa responsable hiérarchique',  '',                    bw);
  sigBox(doc, m + (bw + gap) * 2, y, 'Visa Direction / RH',           '',                    bw);

  /* Pied de page */
  y = doc.internal.pageSize.getHeight() - 8;
  doc.setDrawColor(109, 78, 54);
  doc.line(m, y - 3, pw - m, y - 3);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(160, 140, 120);
  doc.text('Document confidentiel — Usage interne uniquement', pw / 2, y, { align: 'center' });

  doc.save(`demande_numero_${(data.nom_prenom || 'nouveau').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`);
};

/* ═══════════════════════════════════════════════════════════════════════════
   PDF 2 — Fiche de remise de la carte SIM
═══════════════════════════════════════════════════════════════════════════ */
export const generateFicheRemise = (data) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw  = doc.internal.pageSize.getWidth();
  const m   = 14;
  let   y   = pageHeader(doc, 'FICHE DE REMISE DE CARTE SIM',
                          'ShemsPaie — Gestion Téléphonie', pw);

  /* ── Bénéficiaire ── */
  y = sectionHeader(doc, m, y, '1. BÉNÉFICIAIRE', pw) + 4;
  fieldLine(doc, m, y,      'Société',         data.societe);       y += 8;
  fieldLine(doc, m, y,      'Nom & Prénom',    data.nom_prenom);    y += 8;
  fieldLine(doc, m, y,      'Fonction',        data.qualification); y += 8;
  fieldLine(doc, m, y,      'Bureau / Site',   data.bureau);        y += 8;
  y += 4;

  /* ── Carte SIM remise ── */
  y = sectionHeader(doc, m, y, '2. CARTE SIM REMISE', pw) + 4;

  /* Encadré numéro mis en valeur */
  doc.setFillColor(248, 243, 238);
  doc.setDrawColor(109, 78, 54);
  doc.roundedRect(m, y, pw - m * 2, 14, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(109, 78, 54);
  doc.text(data.tel || '_ _ _ _ _ _ _ _ _ _', pw / 2, y + 9, { align: 'center' });
  y += 18;

  fieldLine(doc, m, y,      'Opérateur',       data.operateur);     y += 8;
  fieldLine(doc, m, y,      'Forfait',         data.forfait);       y += 8;
  fieldLine(doc, m, y,      "Type de ligne",   data.colonne1);      y += 8;
  y += 4;

  /* ── Conditions d'utilisation ── */
  y = sectionHeader(doc, m, y, "3. CONDITIONS D'UTILISATION", pw) + 4;
  const conds = [
    "La carte SIM remise est à usage professionnel exclusif.",
    "Toute perte ou vol doit être signalé immédiatement au responsable téléphonie.",
    "La carte SIM reste la propriété de l'entreprise et devra être restituée à la fin du contrat.",
  ];
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  conds.forEach((c, i) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${i + 1}.`, m, y);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(c, pw - m * 2 - 6);
    doc.text(lines, m + 5, y);
    y += lines.length * 4.5 + 1.5;
  });
  y += 4;

  /* ── Date + Signatures ── */
  y = sectionHeader(doc, m, y, '4. DATE ET SIGNATURES', pw) + 6;
  fieldLine(doc, m, y, 'Date de remise', data.date_remise ? fmtDate(data.date_remise) : '');
  y += 12;
  sigBox(doc, pw / 2 - 55, y, 'Signature du bénéficiaire', data.nom_prenom || '', 110);

  /* Pied de page */
  y = doc.internal.pageSize.getHeight() - 8;
  doc.setDrawColor(109, 78, 54);
  doc.line(m, y - 3, pw - m, y - 3);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(160, 140, 120);
  doc.text('Document confidentiel — Usage interne uniquement', pw / 2, y, { align: 'center' });

  doc.save(`fiche_remise_sim_${(data.tel || 'numero').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`);
};

/* ═══════════════════════════════════════════════════════════════════════════
   Modal 1 — Demande nouveau numéro
═══════════════════════════════════════════════════════════════════════════ */
export function DemandeNumeroModal({ show, onHide, societes }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    societe: '', nom_prenom: '', qualification: '', bureau: '',
    operateur: '', type_ligne: '', forfait: '', motif: '', date_demande: today,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGenerate = () => {
    generateDemandeNumero(form);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: `2px solid #f5ede8` }}>
        <Modal.Title style={{ color: MAIN, fontWeight: 700, fontSize: '1rem' }}>
          📋 Demande d'attribution d'un numéro professionnel
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={6}>
            <label style={labelStyle}>Société</label>
            <select style={inputStyle} value={form.societe} onChange={e => set('societe', e.target.value)}>
              <option value="">— Sélectionner —</option>
              {(societes.length ? societes : SOCIETES_LIST).map(s => (
                <option key={s} value={s}>{String(s).toUpperCase()}</option>
              ))}
            </select>
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Date de la demande</label>
            <input type="date" style={inputStyle} value={form.date_demande}
              onChange={e => set('date_demande', e.target.value)} />
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Nom & Prénom <span style={{ color: '#dc3545' }}>*</span></label>
            <input style={inputStyle} value={form.nom_prenom}
              onChange={e => set('nom_prenom', e.target.value)} placeholder="Ex : Mohamed ALAMI" />
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Fonction / Qualification</label>
            <input style={inputStyle} value={form.qualification}
              onChange={e => set('qualification', e.target.value)} placeholder="Ex : Conducteur travaux" />
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Bureau / Site</label>
            <input style={inputStyle} value={form.bureau}
              onChange={e => set('bureau', e.target.value)} />
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Opérateur souhaité</label>
            <select style={inputStyle} value={form.operateur} onChange={e => set('operateur', e.target.value)}>
              <option value="">— Sélectionner —</option>
              {OPERATEURS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Type de ligne</label>
            <select style={inputStyle} value={form.type_ligne} onChange={e => set('type_ligne', e.target.value)}>
              <option value="">— Sélectionner —</option>
              {STATUTS_TYPE.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Forfait souhaité</label>
            <input style={inputStyle} value={form.forfait}
              onChange={e => set('forfait', e.target.value)} placeholder="Ex : 30H + 15Go" />
          </Col>
          <Col xs={12}>
            <label style={labelStyle}>Motif de la demande</label>
            <textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }}
              value={form.motif} onChange={e => set('motif', e.target.value)}
              placeholder="Précisez la raison de cette demande..." />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: '1px solid #f5ede8' }}>
        <button style={btnSm('#888')} onClick={onHide}>Annuler</button>
        <button
          onClick={handleGenerate}
          disabled={!form.nom_prenom}
          style={{ ...btnSm(MAIN, MAIN), opacity: !form.nom_prenom ? 0.5 : 1 }}
        >
          📄 Générer le PDF
        </button>
      </Modal.Footer>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Modal 2 — Fiche remise SIM (peut être pré-remplie depuis une ligne)
═══════════════════════════════════════════════════════════════════════════ */
export function FicheRemiseModal({ show, onHide, prefill, societes, allRows = [] }) {
  const today = new Date().toISOString().slice(0, 10);
  const EMPTY = {
    societe: '', nom_prenom: '', qualification: '', bureau: '',
    tel: '', operateur: '', forfait: '', colonne1: '',
    ancien_utilisateur: '', date_remise: today,
  };

  const [form, setForm]       = useState({ ...EMPTY, ...prefill });
  const [search, setSearch]   = useState('');
  const [open, setOpen]       = useState(false);

  // Sync si prefill change (ouverture depuis ligne)
  React.useEffect(() => {
    if (prefill) setForm({ ...EMPTY, ...prefill, date_remise: today });
    else         setForm({ ...EMPTY });
    setSearch('');
    setOpen(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, show]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyRow = (row) => {
    setForm(f => ({
      ...f,
      societe:            row.societe            || '',
      nom_prenom:         row.nom_prenom         || '',
      qualification:      row.qualification      || '',
      bureau:             row.bureau             || '',
      tel:                row.tel                || '',
      operateur:          row.operateur          || '',
      forfait:            row.forfait            || '',
      colonne1:           row.colonne1           || '',
      ancien_utilisateur: row.ancien_utilisateur || '',
    }));
    setSearch(`${row.tel || ''}${row.nom_prenom ? ' — ' + row.nom_prenom : ''}`);
    setOpen(false);
  };

  const filtered = allRows.filter(r => {
    const q = search.toLowerCase();
    return !q || !open
      ? false
      : (r.tel || '').includes(q) ||
        (r.nom_prenom || '').toLowerCase().includes(q) ||
        (r.societe    || '').toLowerCase().includes(q);
  });

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: `2px solid #f5ede8` }}>
        <Modal.Title style={{ color: MAIN, fontWeight: 700, fontSize: '1rem' }}>
          📱 Fiche de remise de la carte SIM
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* ── Sélecteur numéro existant ── */}
        {allRows.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <label style={{ ...labelStyle, color: MAIN }}>
              🔍 Remplir depuis un numéro existant
            </label>
            <div style={{ position: 'relative' }}>
              <input
                style={{ ...inputStyle, paddingRight: 80 }}
                placeholder="Recherche par numéro, nom ou société…"
                value={search}
                onChange={e => { setSearch(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
              />
              {search && (
                <button
                  onClick={() => { setSearch(''); setOpen(false); }}
                  style={{
                    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                    border: 'none', background: 'none', cursor: 'pointer',
                    color: '#aaa', fontSize: '0.8rem', padding: '2px 6px'
                  }}
                >✕ Vider</button>
              )}
              {open && search.length >= 2 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                  background: '#fff', border: '1px solid #d8c8b8', borderRadius: 7,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto'
                }}>
                  {filtered.length === 0 ? (
                    <div style={{ padding: '10px 14px', color: '#aaa', fontSize: '0.83rem' }}>
                      Aucun résultat
                    </div>
                  ) : filtered.slice(0, 10).map(r => (
                    <div
                      key={r.id}
                      onClick={() => applyRow(r)}
                      style={{
                        padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #f5ede8',
                        fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 10,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fdf9f7'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      <span style={{ fontWeight: 700, fontFamily: 'monospace', color: MAIN, minWidth: 95 }}>
                        {r.tel || '—'}
                      </span>
                      <span style={{ color: '#555' }}>{r.nom_prenom || '—'}</span>
                      {r.societe && (
                        <span style={{
                          marginLeft: 'auto', fontSize: '0.72rem', fontWeight: 700,
                          color: MAIN, background: 'rgba(109,78,54,0.08)',
                          border: '1px solid rgba(109,78,54,0.2)',
                          borderRadius: 5, padding: '1px 6px', textTransform: 'uppercase'
                        }}>{r.societe}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ borderTop: '1px dashed #e8d5c4', margin: '14px 0 4px' }} />
          </div>
        )}

        <Row className="g-3">
          <Col md={6}>
            <label style={labelStyle}>Société</label>
            <select style={inputStyle} value={form.societe} onChange={e => set('societe', e.target.value)}>
              <option value="">— Sélectionner —</option>
              {(societes.length ? societes : SOCIETES_LIST).map(s => (
                <option key={s} value={s}>{String(s).toUpperCase()}</option>
              ))}
            </select>
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Date de remise</label>
            <input type="date" style={inputStyle} value={form.date_remise}
              onChange={e => set('date_remise', e.target.value)} />
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Nom & Prénom bénéficiaire <span style={{ color: '#dc3545' }}>*</span></label>
            <input style={inputStyle} value={form.nom_prenom}
              onChange={e => set('nom_prenom', e.target.value)} />
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Fonction / Qualification</label>
            <input style={inputStyle} value={form.qualification}
              onChange={e => set('qualification', e.target.value)} />
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Bureau / Site</label>
            <input style={inputStyle} value={form.bureau}
              onChange={e => set('bureau', e.target.value)} />
          </Col>
          <Col md={6}>
            <label style={labelStyle}>Numéro remis <span style={{ color: '#dc3545' }}>*</span></label>
            <input style={{ ...inputStyle, fontWeight: 700, fontFamily: 'monospace' }}
              value={form.tel} onChange={e => set('tel', e.target.value)}
              placeholder="Ex : 0662692316" />
          </Col>
          <Col md={4}>
            <label style={labelStyle}>Opérateur</label>
            <select style={inputStyle} value={form.operateur} onChange={e => set('operateur', e.target.value)}>
              <option value="">—</option>
              {OPERATEURS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </Col>
          <Col md={4}>
            <label style={labelStyle}>Forfait</label>
            <input style={inputStyle} value={form.forfait}
              onChange={e => set('forfait', e.target.value)} placeholder="Ex : 30H + 15Go" />
          </Col>
          <Col md={4}>
            <label style={labelStyle}>Type de ligne</label>
            <input style={inputStyle} value={form.colonne1}
              onChange={e => set('colonne1', e.target.value)} placeholder="P / T…" />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: '1px solid #f5ede8' }}>
        <button style={btnSm('#888')} onClick={onHide}>Annuler</button>
        <button
          onClick={() => generateFicheRemise(form)}
          disabled={!form.nom_prenom || !form.tel}
          style={{ ...btnSm(MAIN, MAIN), opacity: (!form.nom_prenom || !form.tel) ? 0.5 : 1 }}
        >
          📄 Générer le PDF
        </button>
      </Modal.Footer>
    </Modal>
  );
}
